// @flow
import { type ActionType, createAction, handleActions } from 'redux-actions';
import produce from 'immer';
import { applyPenders } from 'lib/common';
import * as WishAPI from 'lib/api/me/wish';

/* ACTION TYPE */
const GET_WISH = 'wish/GET_WISH';
const GET_AS_NON_REGISTERED_WISH = 'wish/GET_AS_NON_REGISTERED_WISH';
const ALL_CHECKED_WISH = 'wish/ALL_CHECKED_WISH';
const UPDATE_CHECKED_WISH = 'wish/UPDATE_CHECKED_WISH';
const ADD_ITEM_WISH = 'wish/ADD_ITEM_WISH';
const ADD_ITEM_AS_NON_REGISTERED_WISH = 'wish/ADD_ITEM_AS_NON_REGISTERED_WISH';
const EMPTY_WISH = 'wish/EMPTY_WISH';
const EMPTY_AS_NON_REGISTERED_WISH = 'wish/EMPTY_AS_NON_REGISTERED_WISH';
const COUNT_ITEMS_WISH = 'wish/COUNT_ITEMS_WISH';
const DELETE_ITEM_WISH = 'wish/DELETE_ITEM_WISH';
const DELETE_ITEM_AS_NON_REGISTERED_WISH = 'wish/DELETE_ITEM_AS_NON_REGISTERED_WISH';
const UPDATE_ITEM_WISH = 'wish/UPDATE_ITEM_WISH';
const UPDATE_ITEM_AS_NON_REGISTERED_WISH = 'wish/UPDATE_ITEM_AS_NON_REGISTERED_WISH';
const CHECKOUT_WISH = 'wish/CHECKOUT_WISH';
const CHECKOUT_AS_NON_REGISTERED_WISH = 'wish/CHECKOUT_AS_NON_REGISTERED_WISH';

/* ACTION CREATOR */
const getWish = createAction(GET_WISH, WishAPI.getForMe);
const getAsNonRegisteredWish = createAction(
  GET_AS_NON_REGISTERED_WISH,
  WishAPI.getAsNonRegisteredForMe,
);
const allCheckedWish = createAction(ALL_CHECKED_WISH);
const updateCheckedWish = createAction(UPDATE_CHECKED_WISH);
const addItemWish = createAction(ADD_ITEM_WISH, WishAPI.addItemForMe);
const addItemAsNonRegisteredWish = createAction(
  ADD_ITEM_AS_NON_REGISTERED_WISH,
  payload => payload,
);
const emptyWish = createAction(EMPTY_WISH, WishAPI.emptyForMe);
const emptyAsNonRegisteredWish = createAction(EMPTY_AS_NON_REGISTERED_WISH);
const countItemsWish = createAction(COUNT_ITEMS_WISH, WishAPI.countItemsForMe);
const deleteItemWish = createAction(DELETE_ITEM_WISH, WishAPI.deleteItemForMe);
const deleteItemAsNonRegisteredWish = createAction(
  DELETE_ITEM_AS_NON_REGISTERED_WISH,
  payload => payload,
);
const updateItemWish = createAction(UPDATE_ITEM_WISH, WishAPI.updateItemForMe);
const updateItemAsNonRegisteredWish = createAction(
  UPDATE_ITEM_AS_NON_REGISTERED_WISH,
  payload => payload,
);
const checkoutWish = createAction(CHECKOUT_WISH, WishAPI.checkoutForMe);
const checkoutAsNonRegisteredWish = createAction(
  CHECKOUT_AS_NON_REGISTERED_WISH,
  WishAPI.checkoutAsNonRegisteredForMe,
);

/* ACTION FLOW TYPE */
type GetWishAction = ActionType<typeof getWish>;
type GetAsNonRegisteredWishAction = ActionType<typeof getAsNonRegisteredWish>;
type AllCheckedWishAction = ActionType<typeof allCheckedWish>;
type UpdateCheckedWishAction = ActionType<typeof updateCheckedWish>;
type AddItemWishAction = ActionType<typeof addItemWish>;
type AddItemAsNonRegisteredWishAction = ActionType<typeof addItemAsNonRegisteredWish>;
type EmptyWishAction = ActionType<typeof emptyWish>;
type EmptyAsNonRegisteredWishAction = ActionType<typeof emptyAsNonRegisteredWish>;
type CountItemsWishAction = ActionType<typeof countItemsWish>;
type DeleteItemWishAction = ActionType<typeof deleteItemWish>;
type DeleteItemAsNonRegisteredWishAction = ActionType<typeof deleteItemAsNonRegisteredWish>;
type UpdateItemWishAction = ActionType<typeof updateItemWish>;
type UpdateItemAsNonRegisteredWishAction = ActionType<typeof updateItemAsNonRegisteredWish>;
type CheckoutWishAction = ActionType<typeof checkoutWish>;
type CheckoutAsNonRegisteredWishAction = ActionType<typeof checkoutAsNonRegisteredWish>;

/* ACTION CREATORS INTERFACE */
export interface WishActionCreators {
  getWish(payload: GetWishAction): any;
  getAsNonRegisteredWish(payload: GetAsNonRegisteredWishAction): any;
  allCheckedWish(payload: AllCheckedWishAction): any;
  updateCheckedWish(payload: UpdateCheckedWishAction): any;
  addItemWish(payload: AddItemWishAction): any;
  addItemAsNonRegisteredWish(payload: AddItemAsNonRegisteredWishAction): any;
  emptyWish(payload: EmptyWishAction): any;
  emptyAsNonRegisteredWish(payload: EmptyAsNonRegisteredWishAction): any;
  countItemsWish(payload: CountItemsWishAction): any;
  deleteItemWish(payload: DeleteItemWishAction): any;
  deleteItemAsNonRegisteredWish(payload: DeleteItemAsNonRegisteredWishAction): any;
  updateItemWish(payload: UpdateItemWishAction): any;
  updateItemAsNonRegisteredWish(payload: UpdateItemAsNonRegisteredWishAction): any;
  checkoutWish(payload: CheckoutWishAction): any;
  checkoutAsNonRegisteredWish(payload: CheckoutAsNonRegisteredWishAction): any;
}

/* EXPORT ACTION CREATORS */
export const actionCreators: WishActionCreators = {
  getWish,
  getAsNonRegisteredWish,
  allCheckedWish,
  updateCheckedWish,
  addItemWish,
  addItemAsNonRegisteredWish,
  emptyWish,
  emptyAsNonRegisteredWish,
  countItemsWish,
  deleteItemWish,
  deleteItemAsNonRegisteredWish,
  updateItemWish,
  updateItemAsNonRegisteredWish,
  checkoutWish,
  checkoutAsNonRegisteredWish,
};

/* STATE TYPES */
export type Wish = {
  address: any,
  currency: any,
  errors: any,
  items: any,
  language: any,
  shipments: any,
  status: string,
  tax: any,
  total: any,
  allChecked: boolean,
  applicableCoupons: any,
  checkout: any,
};

/* INITIAL STATE */
const initialState: Wish = {
  address: {
    shipping: {
      address1: '',
      address2: '',
      city: '',
      company: '',
      country: {
        code: '',
        name: '',
      },
      mobile: '',
      name: {
        first: '',
        last: '',
        full: '',
      },
      phone: '',
      postcode: '',
      state: '',
    },
    billing: {
      address1: '',
      address2: '',
      city: '',
      company: '',
      country: {
        code: '',
        name: '',
      },
      mobile: '',
      name: {
        first: '',
        last: '',
        full: '',
      },
      phone: '',
      postcode: '',
      state: '',
    },
  },
  primary: {
    address1: '',
    address2: '',
    city: '',
    company: '',
    country: {
      code: '',
      name: '',
    },
    mobile: '',
    name: {
      first: '',
      last: '',
      full: '',
    },
    phone: '',
    postcode: '',
    state: '',
    email: '',
  },
  currency: {},
  errors: [],
  items: [],
  language: {},
  shipments: [],
  status: '',
  tax: {},
  total: {
    amount: {
      converted: '',
      convertedRow: 0,
      formatted: '',
      raw: 0,
    },
    discounted: {},
    items: {
      discounted: {
        converted: '',
      },
      price: {
        sale: {
          converted: '',
        },
        original: {
          converted: '',
        },
      },
      taxed: {},
    },
    price: {
      sale: {
        converted: '',
      },
    },
    shipping: {
      fee: {
        sale: {
          converted: '',
        },
      },
    },
    taxed: {
      converted: '',
    },
    taxes: {},
  },
  allChecked: true,
  applicableCoupons: [],
  checkout: {},
};

/* REDUCER */
const reducer = handleActions(
  {
    [GET_WISH]: (state, action: GetWishAction) => {
      return produce(state, draft => {
        if (!action) return;
        draft.address = action.payload.address;
        draft.primary = action.payload.primary;
        draft.currency = action.payload.currency;
        draft.errors = action.payload.errors;
        draft.items = action.payload.items.map(item => ({
          ...item,
          checked: true,
        }));
        draft.language = action.payload.language;
        draft.shipments = action.payload.shipments;
        draft.status = action.payload.status;
        draft.tax = action.payload.tax;
        draft.total = action.payload.total;
        draft.allChecked = true;
        draft.applicableCoupons = action.payload.applicableCoupons;
      });
    },
    [GET_AS_NON_REGISTERED_WISH]: (state, action: GetAsNonRegisteredWishAction) => {
      return produce(state, draft => {
        if (!action) return;
        draft.address = action.payload.address;
        draft.primary = action.payload.primary;
        draft.currency = action.payload.currency;
        draft.errors = action.payload.errors;
        draft.items = action.payload.items.map(item => ({
          ...item,
          id: item._id,
          checked: true,
        }));
        draft.language = action.payload.language;
        draft.shipments = action.payload.shipments;
        draft.status = action.payload.status;
        draft.tax = action.payload.tax;
        draft.total = action.payload.total;
        draft.allChecked = true;
        draft.applicableCoupons = action.payload.applicableCoupons;
      });
    },
    [ALL_CHECKED_WISH]: (state, action: AllCheckedWishAction) => {
      return produce(state, draft => {
        if (!action) return;
        draft.items = state.items.map(item => ({
          ...item,
          checked: !state.allChecked,
        }));
        draft.allChecked = !state.allChecked;
      });
    },
    [UPDATE_CHECKED_WISH]: (state, action: UpdateCheckedWishAction) => {
      return produce(state, draft => {
        if (!action) return;
        const index = action.payload;
        draft.items[index].checked = !state.items[index].checked;

        // 개별 체크박스 변경 시 전체선택 상태 업데이트
        const allItemsChecked = draft.items.every(item => item.checked);
        draft.allChecked = allItemsChecked;
      });
    },
    [ADD_ITEM_WISH]: (state, action: AddItemWishAction) => {
      return produce(state, draft => {
        if (!action) return;
        draft.items.push(action.payload.items);
      });
    },
    [ADD_ITEM_AS_NON_REGISTERED_WISH]: (state, { payload }) => {
      return produce(state, draft => {
        window.CacheBy.LocalWish.addItem({
          product: payload.product,
          variant: payload.variant,
          quantity: payload.quantity,
          shippingMethod: payload.shippingMethod,
        });
      });
    },
    [DELETE_ITEM_AS_NON_REGISTERED_WISH]: (state, { payload }) => {
      return produce(state, draft => {
        window.CacheBy.LocalWish.deleteItem(payload.itemId);
        draft.items = state.items.filter(item => {
          return item._id !== payload.itemId;
        });
      });
    },
    [EMPTY_AS_NON_REGISTERED_WISH]: (state, action: EmptyAsNonRegisteredWishAction) => {
      window.CacheBy.LocalWish.empty();
      return produce(state, draft => {
        if (!action) return;
        draft.items = [];
      });
    },
    [UPDATE_ITEM_AS_NON_REGISTERED_WISH]: (state, { payload }) => {
      return produce(state, draft => {
        window.CacheBy.LocalWish.updateItem(payload.itemId, {
          quantity: payload.quantity,
        });
        draft.items = state.items.map(item => {
          if (item._id === payload.itemId) {
            item.quantity = payload.quantity;
          }
          return item;
        });
        draft.items = state.items.map(item => {
          if (item._id === payload.itemId) {
            item.quantity = payload.quantity;
          }
          return item;
        });
      });
    },
  },
  initialState,
);

export default applyPenders(reducer, [
  {
    type: GET_WISH,
    onSuccess: (state, { payload: { data } }) => {
      return produce(state, draft => {
        draft.address = data.address;
        draft.primary = data.primary;
        draft.currency = data.currency;
        draft.errors = data.errors;
        draft.items = data.items.map(item => ({
          ...item,
          checked: true,
        }));
        draft.language = data.language;
        draft.shipments = data.shipments;
        draft.status = data.status;
        draft.tax = data.tax;
        draft.total = data.total;
        draft.allChecked = true;
        draft.applicableCoupons = data.applicableCoupons;
      });
    },
  },
  {
    type: GET_AS_NON_REGISTERED_WISH,
    onSuccess: (state, { payload: { data } }) => {
      return produce(state, draft => {
        draft.address = data.address;
        draft.primary = data.primary;
        draft.currency = data.currency;
        draft.errors = data.errors;
        draft.items = data.items.map(item => ({
          ...item,
          id: item._id,
          checked: true,
        }));
        draft.language = data.language;
        draft.shipments = data.shipments;
        draft.status = data.status;
        draft.tax = data.tax;
        draft.total = data.total;
        draft.allChecked = true;
        draft.applicableCoupons = data.applicableCoupons;
      });
    },
  },
  {
    type: EMPTY_WISH,
    onSuccess: (state, { payload: { data } }) => {
      return produce(state, draft => {
        draft.items = [];
      });
    },
  },
  {
    type: DELETE_ITEM_WISH,
    onSuccess: (state, { payload: { data } }) => {
      return produce(state, draft => {
        draft.items = state.items.filter(item => {
          return item.id !== data;
        });
      });
    },
  },
  {
    type: UPDATE_ITEM_WISH,
    onSuccess: (state, { payload: { data } }) => {
      return produce(state, draft => {
        draft.items = state.items.map(item => {
          if (item.id === data.id) {
            item.quantity = data.quantity;
          }
          return item;
        });
      });
    },
  },
  {
    type: CHECKOUT_WISH,
    onSuccess: (state, { payload: { data } }) => {
      return produce(state, draft => {
        draft.checkout = data;
      });
    },
  },
  {
    type: CHECKOUT_AS_NON_REGISTERED_WISH,
    onSuccess: (state, { payload: { data } }) => {
      return produce(state, draft => {
        draft.checkout = data;
      });
    },
  },
]);



// WEBPACK FOOTER //
// ./src/store/modules/wish.js